import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {Button, Popover, Stack, Tooltip} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {useState, type ReactNode} from "react";
import {FLOATING_SHADOW} from "../../newVariablePicker/shared/floatingShadow";
import ToolbarButton from "./ToolbarButton";

export interface ToolbarSelectOption<V extends string> {
    value: V;
    label: string;
    icon: ReactNode;
}

interface Props<V extends string> {
    /** What the choice is about: the button's tooltip and accessible name. */
    label: string;
    value: V;
    /** The first option is the default; any other one shows the button as set, like a format that is on. */
    options: readonly ToolbarSelectOption<V>[];
    onChange: (value: V) => void;
}

/**
 * One toolbar button for a set of exclusive choices, the way text editors do alignment: it shows the
 * current choice, and opens all of them in a row.
 */
export default function ToolbarSelect<V extends string>({label, value, options, onChange}: Props<V>) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const current = options.find(option => option.value === value) ?? options[0];
    const active = !!anchor || current.value !== options[0].value;

    return (
        <>
            <Tooltip title={label}>
                <Button
                    aria-label={label}
                    aria-haspopup={'true'}
                    aria-expanded={!!anchor}
                    // The text keeps its focus and its selection.
                    onMouseDown={event => event.preventDefault()}
                    onClick={event => setAnchor(event.currentTarget)}
                    sx={theme => ({
                        minWidth: 0,
                        height: 32,
                        px: 0.5,
                        borderRadius: 1,
                        color: active ? 'primary.main' : 'text.secondary',
                        bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                    })}
                >
                    {current.icon}
                    <KeyboardArrowDownIcon sx={{fontSize: 16}}/>
                </Button>
            </Tooltip>

            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                transitionDuration={0}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                slotProps={{paper: {variant: 'outlined', sx: {mt: '8px', p: 0.5, borderRadius: 2, boxShadow: FLOATING_SHADOW}}}}
            >
                <Stack direction={'row'} gap={0.25} role={'group'} aria-label={label}>
                    {options.map(option => (
                        <ToolbarButton
                            key={option.value}
                            label={option.label}
                            icon={option.icon}
                            active={option.value === current.value}
                            onClick={() => {
                                setAnchor(null);
                                onChange(option.value);
                            }}
                        />
                    ))}
                </Stack>
            </Popover>
        </>
    );
}
